import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './RequestActionModal.css';
import { FaTimes, FaCheck, FaTimesCircle, FaEnvelope } from 'react-icons/fa';
import { BiErrorCircle } from 'react-icons/bi';
import { sendVacationAcceptedEmail, sendVacationDeclinedEmail } from '../components/emailService';

function RequestActionModal({ mode, request, onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState('confirmation');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employeeEmail, setEmployeeEmail] = useState(null);
  const [specificDays, setSpecificDays] = useState([]);
  const [rhEmails, setRhEmails] = useState([]);
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  
  // Load necessary data when modal opens
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get the employee email
        const { data: employee, error: employeeError } = await supabase
          .from('employees')
          .select('wsp_email, full_name')
          .eq('id', request.employee_id)
          .single();
          
        if (employeeError) throw employeeError;
        setEmployeeEmail(employee.wsp_email);
        
        // Get RH emails for accepted requests
        if (mode === 'accept') {
          const { data: rhUsers, error: rhError } = await supabase
            .from('users')
            .select('wsp_email')
            .eq('role', 'RH');
            
          if (rhError) throw rhError;
          setRhEmails(rhUsers.map(user => user.wsp_email));
          
          // Get specific days
          const { data: days, error: daysError } = await supabase
            .from('vacation_requests_days')
            .select('vacation_date')
            .eq('request_id', request.id)
            .order('vacation_date', { ascending: true });
            
          if (daysError) throw daysError;
          setSpecificDays(days);
        } else if (mode === 'decline') {
            // Also get specific days for decline mode
            const { data: days, error: daysError } = await supabase
            .from('vacation_requests_days')
            .select('vacation_date')
            .eq('request_id', request.id)
            .order('vacation_date', { ascending: true });
            
          if (daysError) throw daysError;
          setSpecificDays(days);
        }
        
        // Get current user's email
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          setCurrentUserEmail(user.email);
        }
      } catch (err) {
        console.error('Error fetching data for email:', err);
        setError('Error al preparar el correo electrónico: ' + err.message);
      }
    };
    
    fetchData();
  }, [request.id, request.employee_id, mode]);
  
  // Handle accepting the vacation request
  const handleAcceptRequest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // 1. Fetch employee data
      const { data: employee, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .eq('id', request.employee_id)
        .single();
        
      if (employeeError) throw employeeError;
      
      // 2. Get all the specific days from the request
      const { data: requestDays, error: requestDaysError } = await supabase
        .from('vacation_requests_days')
        .select('*')
        .eq('request_id', request.id);
        
      if (requestDaysError) throw requestDaysError;
      
      // Calculate the number of days to deduct
      const daysToDeduct = request.is_half_day ? 0.5 : requestDays.length;
      
      // 3. Update employee remaining_days and days_taken
      const newRemainingDays = employee.remaining_days - daysToDeduct;
      const newDaysTaken = (employee.days_taken || 0) + daysToDeduct;
      
      await supabase
        .from('employees')
        .update({
          remaining_days: newRemainingDays,
          days_taken: newDaysTaken
        })
        .eq('id', employee.id);
      
      // 4. Handle overtime days logic
      const { data: overtimeData } = await supabase
        .from('overtime_vacation_days')
        .select('days_remaining')
        .eq('employee_id', employee.id)
        .single();
      
      if (overtimeData && overtimeData.days_remaining !== null) {
        if (daysToDeduct >= overtimeData.days_remaining) {
          // If using all or more than available overtime days, set to null
          await supabase
            .from('overtime_vacation_days')
            .update({ days_remaining: null, expiration_date: null })
            .eq('employee_id', employee.id);
        } else {
          // Otherwise, subtract the days
          const newOvertimeDays = overtimeData.days_remaining - daysToDeduct;
          await supabase
            .from('overtime_vacation_days')
            .update({ days_remaining: newOvertimeDays })
            .eq('employee_id', employee.id);
        }
      }
      
      // 5. Insert the vacation days into the vacation_days table
      const vacationDaysData = requestDays.map(day => ({
        employee_id: employee.id,
        vacation_date: day.vacation_date,
        has_half_day: request.is_half_day
      }));
      
      await supabase
        .from('vacation_days')
        .insert(vacationDaysData);
      
      // 6. Update the request status to accepted and set accepted_date
      const today = new Date().toISOString().split('T')[0];
      await supabase
        .from('vacation_requests')
        .update({
          status: true,
          accepted_date: today
        })
        .eq('id', request.id);
      
      // Move to success step
      setCurrentStep('success');
      
      // Notify parent component of the successful action
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error accepting request:', error);
      setError('Error al aceptar la solicitud: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle declining the vacation request
  const handleDeclineRequest = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Move to the decline confirmation step
      setCurrentStep('declineConfirmation');
    } catch (error) {
      console.error('Error declining request:', error);
      setError('Error al declinar la solicitud: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle final confirmation of declining the request
  const handleFinalDecline = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Delete the request
      const { error: deleteError } = await supabase
        .from('vacation_requests')
        .delete()
        .eq('id', request.id);
        
      if (deleteError) throw deleteError;
      
      // Notify parent component of the successful action
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the modal
      onClose();
      
    } catch (error) {
      console.error('Error deleting request:', error);
      setError('Error al eliminar la solicitud: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Format date for the email
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    // Add one day to compensate for timezone
    date.setDate(date.getDate() + 1);
    
    const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    const weekday = weekdays[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${weekday} ${day} de ${month} del ${year}`;
  };
  
  // Handle email sending based on the mode
  const handleSendEmail = async () => {
    try {
      if (mode === 'accept') {
        if (!employeeEmail) {
          throw new Error('No se pudo obtener el correo del empleado');
        }
        
        // For accept mode, get employee information
        const { data: employee, error: employeeError } = await supabase
          .from('employees')
          .select('full_name')
          .eq('id', request.employee_id)
          .single();
          
        if (employeeError) throw employeeError;
        
        // Create formatted dates list
        const formattedDates = specificDays.map(day => formatDate(day.vacation_date));
        
        // Send email to employee, the manager (current user), and all RH users
        const recipients = [employeeEmail];
        if (currentUserEmail && !recipients.includes(currentUserEmail)) {
          recipients.push(currentUserEmail);
        }
        rhEmails.forEach(rhEmail => {
          if (rhEmail && !recipients.includes(rhEmail)) {
            recipients.push(rhEmail);
          }
        });
        
        // Call the email service function - now async
        await sendVacationAcceptedEmail({
          recipients: recipients,
          employeeName: employee.full_name,
          vacationDays: request.requested_days,
          specificDates: formattedDates,
          approverEmail: currentUserEmail
        });
        
      } else if (mode === 'decline') {
        if (!employeeEmail) {
          throw new Error('No se pudo obtener el correo del empleado');
        }
        
        // For decline mode, we also need employee info now
        const { data: employee, error: employeeError } = await supabase
          .from('employees')
          .select('full_name')
          .eq('id', request.employee_id)
          .single();
          
        if (employeeError) throw employeeError;

        // Create formatted dates list
        console.log('Specific days for decline:', specificDays);
        const formattedDates = specificDays.map(day => formatDate(day.vacation_date));
        console.log('Formatted dates for decline:', formattedDates);

        // For decline mode, send to the employee and the manager (current user)
        const declineRecipients = [employeeEmail];
        if (currentUserEmail && !declineRecipients.includes(currentUserEmail)) {
          declineRecipients.push(currentUserEmail);
        }

        await sendVacationDeclinedEmail({
            recipients: declineRecipients,
            employeeName: employee.full_name,
            vacationDays: request.requested_days,
            specificDates: formattedDates,
            approverEmail: currentUserEmail
        });
      }
      
      // Removed the alert here because it's now handled inside the email service functions
      
    } catch (error) {
      console.error('Error sending email:', error);
      // Alert is already shown in catch block of email service, but if error happens before that (e.g. employee lookup), show it here
      if (!error.message.includes('Power Automate')) {
          alert('Error al enviar el correo: ' + error.message);
      }
    }
  };

  return (
    <div className="request-action-modal-overlay">
      <div className={`request-action-modal-content ${mode}-mode`}>
        {/* Header */}
        <div className={`request-action-modal-header ${mode}-header`}>
          <h2>
            {mode === 'accept' ? (
              <>
                <FaCheck className="modal-icon" /> Aceptar Solicitud
              </>
            ) : (
              <>
                <FaTimesCircle className="modal-icon" /> Declinar Solicitud
              </>
            )}
          </h2>
          <button className="request-action-close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        {/* Body */}
        <div className="request-action-modal-body">
          {error && (
            <div className="error-message">
              <BiErrorCircle className="error-icon" />
              {error}
            </div>
          )}
          
          {/* Accept Mode - Confirmation Step */}
          {mode === 'accept' && currentStep === 'confirmation' && (
            <div className="confirmation-content">
              <p className="confirmation-message">¿Está seguro de aceptar la solicitud?</p>
              <p className="employee-name">{request.employee_name}</p>
              <p className="request-details">
                {request.requested_days} {request.requested_days === 1 ? 'día' : 'días'} solicitados
                ({request.is_half_day ? 'Medio día' : 'Día/s completos'})
              </p>
              
              <div className="action-buttons">
                <button
                  className="request-action-confirm-button"
                  onClick={handleAcceptRequest}
                  disabled={loading}
                >
                  {loading ? 'Procesando...' : 'Sí'}
                </button>
                <button
                  className="request-action-cancel-button"
                  onClick={onClose}
                  disabled={loading}
                >
                  No
                </button>
              </div>
            </div>
          )}
          
          {/* Accept Mode - Success Step */}
          {mode === 'accept' && currentStep === 'success' && (
            <div className="success-content">
              <div className="success-icon-container">
                <FaCheck className="success-icon" />
              </div>
              <h3 className="success-title">La solicitud ha sido aceptada correctamente</h3>
              <p className="success-message">
                <strong>Importante:</strong> Haz click en el siguiente botón para enviar automáticamente un correo 
                de confirmación al empleado, a Recursos Humanos y a ti mismo como respaldo.
                Este correo es necesario ya que le indicará al empleado y a Recursos Humanos que la solicitud fue aprobada.
              </p>
              
              <div className="action-buttons">
                <button className="request-action-email-button" onClick={handleSendEmail}>
                  <FaEnvelope className="email-icon" /> Generar correo
                </button>
                <button className="request-action-close-button-text" onClick={onClose}>
                  Cerrar
                </button>
              </div>
            </div>
          )}
          
          {/* Decline Mode - Confirmation Step */}
          {mode === 'decline' && currentStep === 'confirmation' && (
            <div className="confirmation-content">
              <p className="confirmation-message">¿Está seguro de querer declinar esta solicitud?</p>
              <p className="employee-name">{request.employee_name}</p>
              <p className="request-details">
                {request.requested_days} {request.requested_days === 1 ? 'día' : 'días'} solicitados
                ({request.is_half_day ? 'Medio día' : 'Día/s completos'})
              </p>
              
              <div className="action-buttons">
                <button
                  className="request-action-confirm-button"
                  onClick={handleDeclineRequest}
                  disabled={loading}
                >
                  {loading ? 'Procesando...' : 'Sí'}
                </button>
                <button
                  className="request-action-cancel-button"
                  onClick={onClose}
                  disabled={loading}
                >
                  No
                </button>
              </div>
            </div>
          )}
          
          {/* Decline Mode - DeclineConfirmation Step */}
          {mode === 'decline' && currentStep === 'declineConfirmation' && (
            <div className="decline-confirmation-content">
              <p className="decline-message">
                Entendido, la solicitud será declinada. Haz click en "Generar correo" para enviar automáticamente 
                un correo de notificación al empleado informándole que su solicitud fue rechazada. 
                También puedes avisarle por medio de Microsoft Teams.
              </p>
              
              <div className="action-buttons">
                <button className="request-action-email-button" onClick={handleSendEmail}>
                  <FaEnvelope className="email-icon" /> Generar correo
                </button>
                <button
                  className="request-action-confirm-button"
                  onClick={handleFinalDecline}
                  disabled={loading}
                >
                  {loading ? 'Procesando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RequestActionModal;